// The auth-aware chat SSE reader — the load-bearing realtime piece.
//
// We deliberately do NOT use the browser `EventSource`: it cannot attach the
// `Authorization: Bearer` header the stream is authed on. Instead we issue an
// authed `fetch` and read `response.body` as a stream, parsing the SSE wire
// format ourselves. Both `fetch` and a `ReadableStream` body exist in modern
// web AND React Native, so this reader ports to RN unchanged — which is the
// whole reason it lives in the shared layer. The base URL, the fetch impl,
// and the token source are injected (config + store).

import type {
  BrainEvent,
  ChatMessage,
  PreferencesResponse,
  SessionPresence,
  Thread,
  TraceStreamEvent,
} from "./types";
import type { ClientConfig } from "./config";
import { callFetch } from "./config";

/** One parsed SSE event: its `event:` name (default "message" per the SSE
 * spec) and the concatenated `data:` payload. */
export interface SseEvent {
  event: string;
  data: string;
}

/**
 * Parse a buffer of raw SSE text into complete events plus the trailing
 * partial chunk (a frame that hasn't terminated with a blank line yet). Pure
 * + total so the frame parsing is unit-tested without a live stream. Events
 * are separated by a blank line; within an event, `event:` names it and one
 * or more `data:` lines are joined with "\n" (per the SSE spec). Lines that
 * are blank-only, comments (`:`-prefixed), or carry no recognised field are
 * ignored. `\r\n` and `\n` line endings are both handled.
 */
export function parseSseBuffer(buffer: string): {
  events: SseEvent[];
  rest: string;
} {
  // Normalise CRLF → LF so frame splitting is line-ending agnostic.
  const normalised = buffer.replace(/\r\n/g, "\n");
  const events: SseEvent[] = [];
  let cursor = 0;
  for (;;) {
    const boundary = normalised.indexOf("\n\n", cursor);
    if (boundary === -1) break;
    const frame = normalised.slice(cursor, boundary);
    cursor = boundary + 2;
    const parsed = parseSseFrame(frame);
    if (parsed) events.push(parsed);
  }
  return { events, rest: normalised.slice(cursor) };
}

/** Parse a single SSE frame (the text between blank lines) into an event, or
 * null when it carries no `data`. Comments (`:`-prefixed) are ignored. */
function parseSseFrame(frame: string): SseEvent | null {
  let event = "message";
  const dataLines: string[] = [];
  for (const line of frame.split("\n")) {
    if (line === "" || line.startsWith(":")) continue;
    const colon = line.indexOf(":");
    const field = colon === -1 ? line : line.slice(0, colon);
    // A single leading space after the colon is stripped, per the spec.
    let value = colon === -1 ? "" : line.slice(colon + 1);
    if (value.startsWith(" ")) value = value.slice(1);
    if (field === "event") event = value;
    else if (field === "data") dataLines.push(value);
  }
  if (dataLines.length === 0) return null;
  return { event, data: dataLines.join("\n") };
}

/** Backoff schedule (ms) for SSE reconnects, capped at the last entry. */
const SSE_BACKOFF_MS = [1000, 2000, 5000, 10000, 30000];

/**
 * Connection health, lifted OUT of the reader closure (Brief 5 / pt10 + pt20 —
 * stream honesty). The reader self-reconnects silently, so a dead feed would be
 * indistinguishable from a quiet-but-live one — minting false trust. This signal
 * makes the system honest about not knowing:
 *
 *   - `connecting`   — initial, before the first successful connect.
 *   - `live`         — connected and reading (attempt counter reset).
 *   - `reconnecting` — dropped, and a backoff retry is scheduled.
 *   - `dead`         — a hard auth failure (401); the token is gone, no retry.
 *
 * `live` is the ONLY state the UI may render as calm; the others must be visibly
 * honest (dimmed region, a quiet pill) so a stale stream looks stale.
 */
export type StreamHealth = "connecting" | "live" | "reconnecting" | "dead";

/**
 * Build the chat SSE subscriber bound to a config (base URL + fetch + the
 * session-clear hook) and its store. The returned `subscribeChat` opens the
 * stream and dispatches live events until unsubscribed.
 *
 * `onMessage` fires for each `message` frame (a new `ChatMessage`), `onThread`
 * for each `thread` frame (a new/updated `Thread`), and the optional
 * `onPresence` for each `presence` frame (a session's live status flip —
 * Brief 3 D1; subscribers that don't care simply omit it). Malformed JSON in
 * a frame is skipped rather than tearing down the stream. The stream
 * auto-reconnects with capped exponential backoff on any drop; the returned
 * function aborts the in-flight request and halts reconnection. On a hard 401
 * the session is cleared and the stream stops (no reconnect on a dead token).
 *
 * The optional final `onHealth` (Brief 5 / pt10 + pt20) reports the connection
 * health — `connecting` → `live` on a good connect, `reconnecting` on a drop +
 * scheduled retry, `dead` on a hard 401. It is additive: every existing caller
 * omits it and behaves exactly as before. Repeats are coalesced so a caller only
 * sees real transitions.
 */
export function createChatSubscriber(
  config: ClientConfig,
): (
  onMessage: (m: ChatMessage) => void,
  onThread: (t: Thread) => void,
  onPresence?: (p: SessionPresence) => void,
  onTrace?: (t: TraceStreamEvent) => void,
  onPreferences?: (p: PreferencesResponse) => void,
  onBrain?: (b: BrainEvent) => void,
  onHealth?: (h: StreamHealth) => void,
) => () => void {
  const { store, apiBase } = config;

  return function subscribeChat(
    onMessage: (m: ChatMessage) => void,
    onThread: (t: Thread) => void,
    onPresence?: (p: SessionPresence) => void,
    onTrace?: (t: TraceStreamEvent) => void,
    onPreferences?: (p: PreferencesResponse) => void,
    onBrain?: (b: BrainEvent) => void,
    onHealth?: (h: StreamHealth) => void,
  ): () => void {
    let closed = false;
    let attempt = 0;
    let controller: AbortController | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

    // Coalesce health so a caller only hears real transitions, never a repeat
    // (e.g. two consecutive failed reconnects both schedule "reconnecting").
    let lastHealth: StreamHealth | null = null;
    const setHealth = (h: StreamHealth) => {
      if (closed || h === lastHealth) return;
      lastHealth = h;
      onHealth?.(h);
    };

    const dispatch = (evt: SseEvent) => {
      let payload: unknown;
      try {
        payload = JSON.parse(evt.data);
      } catch {
        return; // skip a malformed frame, keep the stream alive
      }
      if (evt.event === "message") onMessage(payload as ChatMessage);
      else if (evt.event === "thread") onThread(payload as Thread);
      else if (evt.event === "presence" && onPresence)
        onPresence(payload as SessionPresence);
      else if (evt.event === "trace" && onTrace)
        onTrace(payload as TraceStreamEvent);
      else if (evt.event === "preferences" && onPreferences)
        onPreferences(payload as PreferencesResponse);
      else if (evt.event === "brain" && onBrain) onBrain(payload as BrainEvent);
    };

    const scheduleReconnect = () => {
      if (closed) return;
      // Honest about the drop: we are no longer live, a retry is pending.
      setHealth("reconnecting");
      const delay = SSE_BACKOFF_MS[Math.min(attempt, SSE_BACKOFF_MS.length - 1)];
      attempt += 1;
      reconnectTimer = setTimeout(() => void connect(), delay);
    };

    async function connect(): Promise<void> {
      if (closed) return;
      controller = new AbortController();
      const headers: Record<string, string> = { Accept: "text/event-stream" };
      const token = store.getToken();
      if (token) headers["Authorization"] = `Bearer ${token}`;

      try {
        const resp = await callFetch(config, `${apiBase}/chat/stream`, {
          headers,
          signal: controller.signal,
        });
        if (!resp.ok || !resp.body) {
          // 401 → the session is dead; clear it so the app re-auths, then stop
          // hammering the dead token (don't reconnect on a hard auth failure).
          if (resp.status === 401) {
            setHealth("dead");
            store.clear();
            config.onSessionCleared?.();
            return;
          }
          scheduleReconnect();
          return;
        }
        // Connected: reset the backoff so the next drop starts from the top.
        attempt = 0;
        setHealth("live");
        const reader = resp.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        for (;;) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const { events, rest } = parseSseBuffer(buffer);
          buffer = rest;
          for (const evt of events) dispatch(evt);
        }
        // Stream ended cleanly from the server side → reconnect.
        if (!closed) scheduleReconnect();
      } catch {
        // Network error / abort. An intentional abort sets `closed`, so this
        // only reconnects on a real drop.
        if (!closed) scheduleReconnect();
      }
    }

    setHealth("connecting");
    void connect();

    return () => {
      closed = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      controller?.abort();
    };
  };
}
